import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import BottomNav from "../../components/BottomNav";
import api, { getImagemComunicadoUrl, resolverImagensConteudoComunicado } from "../../services/api";
import styles from "./comunicado.module.css";
import ApiButton from "../../components/ApiButton/ApiButton";
import BackIcon from "../../components/BackIcon";

// Aceita tanto "dd/mm/yyyy hh:mm:ss" (formato que datger/datcom sempre
// deveriam vir) quanto "yyyy-mm-dd hh:mm:ss"/ISO (caso o backend mande cru) -
// sem isso, uma data no formato errado caía direto no fallback e mostrava a
// string crua sem formatar nenhuma.
function parseDataFlexivel(data) {
    const br = data.match(/^(\d{2})\/(\d{2})\/(\d{4})[ T]?(\d{2}:\d{2}(:\d{2})?)?/);
    if (br) {
        const [, d, m, y, hms] = br;
        const dt = new Date(`${y}-${m}-${d}T${hms || '00:00:00'}`);
        return isNaN(dt) ? null : dt;
    }
    const dt = new Date(data.replace(' ', 'T'));
    return isNaN(dt) ? null : dt;
}

// Mesma regra do FeedCard.jsx: comunicado AGENDADO mostra a data/hora do
// agendamento (datcom+horcom), não a de criação (datger) - pedido do
// usuário, 2026-10-20.
function dataExibidaFeed(post) {
    if (post.tipcom === 'agendado' && post.datcom) {
        return `${post.datcom} ${post.horcom || '00:00'}`;
    }
    return post.datger;
}

function tempoRelativo(data) {
    if (!data) return '';
    const date = parseDataFlexivel(data);
    if (!date) return data;
    const diff = Date.now() - date.getTime();
    const mins  = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days  = Math.floor(diff / 86400000);
    if (mins  <  2) return 'agora';
    if (mins  < 60) return `há ${mins}min`;
    if (hours < 24) return `há ${hours}h`;
    if (days  ===1) return 'ontem';
    if (days  <  7) return `há ${days} dias`;
    return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
}

export default function Comunicado() {
    const { user } = useContext(AuthContext);
    const { state } = useLocation();
    const navigate = useNavigate();

    const [post, setPost] = useState(state?.post ?? null);
    const [comentarios, setComentarios] = useState([]);
    const [loadingComentarios, setLoadingComentarios] = useState(true);
    const [novoComentario, setNovoComentario] = useState("");
    const [enviando, setEnviando] = useState(false);

    useEffect(() => {
        if (!post) { navigate("/home", { replace: true }); return; }
        async function carregarComentarios() {
            try {
                const res = await api.get('/colaborador/comentarios', { params: { codcom: post.codcom } });
                setComentarios(Array.isArray(res.data) ? res.data : []);
            } catch (err) {
                console.error(err);
            } finally {
                setLoadingComentarios(false);
            }
        }
        carregarComentarios();
    }, []);

    async function toggleReacao(tipo) {
        try {
            await api.post(`/colaborador/interacao/?tipfun=${user.tipfun}&codfun=${user.codfun}&tipint=${tipo}&codcom=${post.codcom}`);
            setPost(prev => {
                if (tipo === "curcom") {
                    const jaCurtiu = prev.curcom > 0;
                    return { ...prev, curcom: jaCurtiu ? 0 : 1, curtidas: jaCurtiu ? prev.curtidas - 1 : prev.curtidas + 1 };
                }
                if (tipo === "amecom") {
                    const jaAmou = prev.amecom > 0;
                    return { ...prev, amecom: jaAmou ? 0 : 1, ameis: jaAmou ? prev.ameis - 1 : prev.ameis + 1 };
                }
                return prev;
            });
        } catch (err) {
            console.error(err);
        }
    }

    async function enviarComentario() {
        if (!novoComentario.trim()) return;
        try {
            setEnviando(true);
            await api.post('/colaborador/comentario', null, {
                params: { tipfun: user.tipfun, codfun: user.codfun, codcom: post.codcom, texto: novoComentario },
            });
            setComentarios(prev => [
                ...prev,
                { nomfun: user.nomfun, texmen: novoComentario, datmen: new Date().toLocaleDateString("pt-BR") }
            ]);
            setPost(prev => ({ ...prev, mensagens: (prev.mensagens || 0) + 1 }));
            setNovoComentario("");
        } catch (err) {
            console.error(err);
        } finally {
            setEnviando(false);
        }
    }

    if (!post) return null;

    const totalReacoes = (post.curtidas ?? 0) + (post.ameis ?? 0);

    return (
        <div className={styles.container}>

            {/* HEADER */}
            <div className={styles.header}>
                <button className={styles.btnVoltar} onClick={() => navigate(-1)}><BackIcon /></button>
                <div className={styles.headerTexto}>
                    <h2>{post.setcom}</h2>
                    <p>{tempoRelativo(dataExibidaFeed(post))}</p>
                </div>
            </div>

            {/* CARD PRINCIPAL */}
            <div className={styles.card}>

                {/* TÍTULO + SUBTÍTULO */}
                <div className={styles.cardBody}>
                    <h2 className={styles.titulo} dangerouslySetInnerHTML={{ __html: post.titcom }} />
                    {post.subcom && <p className={styles.subtitulo} dangerouslySetInnerHTML={{ __html: post.subcom }} />}
                </div>

                {/* IMAGEM full-bleed */}
                {post.urlimg && (
                    <img src={getImagemComunicadoUrl(post.codcom)} className={styles.imagem} alt={post.titcom?.replace(/<[^>]*>/g, '')} />
                )}

                {/* TEXTO COMPLETO */}
                {post.concom && (
                    <div
                        className={styles.textoCompleto}
                        dangerouslySetInnerHTML={{ __html: resolverImagensConteudoComunicado(post.concom) }}
                    />
                )}

                {/* BARRA DE REAÇÕES estilo Facebook */}
                {(totalReacoes > 0 || post.mensagens > 0) && (
                    <div className={styles.reactionBar}>
                        <span className={styles.reactionLeft}>
                            {post.curtidas > 0 && '👍'}
                            {post.ameis > 0 && '❤️'}
                            {totalReacoes > 0 && <span>{totalReacoes}</span>}
                        </span>
                        <span className={styles.reactionRight}>
                            {post.mensagens > 0
                                ? `${post.mensagens} comentário${post.mensagens !== 1 ? 's' : ''}`
                                : ''
                            }
                        </span>
                    </div>
                )}

                {/* BOTÕES DE AÇÃO */}
                <div className={styles.acoes}>
                    <ApiButton
                        className={`${styles.btnAcao} ${post.curcom > 0 ? styles.ativo : ''}`}
                        onClick={() => toggleReacao("curcom")}
                    >
                        👍 Curtir
                    </ApiButton>
                    <ApiButton
                        className={`${styles.btnAcao} ${post.amecom > 0 ? styles.ativoAmei : ''}`}
                        onClick={() => toggleReacao("amecom")}
                    >
                        ❤️ Amei
                    </ApiButton>
                </div>
            </div>

            {/* COMENTÁRIOS */}
            <div className={styles.secaoComentarios}>

                <p className={styles.comentariosTitulo}>
                    💬 Comentários{post.mensagens > 0 ? ` (${post.mensagens})` : ''}
                </p>

                {/* INPUT — oculto quando comentários bloqueados */}
                {post.bloqcom ? (
                    <div className={styles.comentariosBloqueados}>
                        🔒 Comentários desabilitados nesta publicação.
                    </div>
                ) : (
                <div className={styles.inputComentario}>
                    <div className={styles.inputAvatar}>{user.nomfun?.charAt(0)}</div>
                    <input
                        type="text"
                        placeholder="Escreva um comentário..."
                        value={novoComentario}
                        onChange={e => setNovoComentario(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && enviarComentario()}
                    />
                    <ApiButton onClick={enviarComentario} disabled={enviando || !novoComentario.trim()}>
                        {enviando ? "..." : "Enviar"}
                    </ApiButton>
                </div>
                )}

                {/* LISTA */}
                {loadingComentarios ? (
                    <div className={styles.skeletonWrap}>
                        {[1, 2, 3].map(i => (
                            <div key={i} className={styles.skeletonItem}>
                                <div className={styles.skeletonAvatar} />
                                <div className={styles.skeletonLines}>
                                    <div className={styles.skeletonLine} style={{ width: '40%' }} />
                                    <div className={styles.skeletonLine} style={{ width: '80%' }} />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : comentarios.length === 0 ? (
                    <p className={styles.semComentarios}>Nenhum comentário ainda. Seja o primeiro!</p>
                ) : (
                    <div className={styles.listaComentarios}>
                        {comentarios.map((c, i) => (
                            <div key={i} className={styles.comentarioItem}>
                                <div className={styles.comentarioAvatar}>
                                    {c.nomfun?.charAt(0) ?? '?'}
                                </div>
                                <div className={styles.comentarioConteudo}>
                                    <div className={styles.comentarioHeader}>
                                        <strong>{c.nomfun}</strong>
                                        <span className={styles.comentarioData}>{c.datmen}</span>
                                    </div>
                                    <p>{c.texmen}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <BottomNav />
        </div>
    );
}
